import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SidebarContextType {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  windowWidth: number;
  isMobile: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 700;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Обновляем CSS переменную для высоты viewport только на мобильных устройствах
      if (window.innerWidth <= 768) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      } else {
        // На ПК очищаем переменную
        document.documentElement.style.removeProperty('--vh');
      }
    };
    
    // Устанавливаем начальное значение
    handleResize();
    
    window.addEventListener('resize', handleResize);
    // Добавляем также обработчик для orientationchange на мобильных
    window.addEventListener('orientationchange', () => {
      // Задержка для корректного определения новой высоты после поворота
      setTimeout(handleResize, 100);
    });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      // Очищаем CSS переменную при размонтировании
      document.documentElement.style.removeProperty('--vh');
    };
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const setSidebarOpen = (open: boolean) => {
    setIsSidebarOpen(open);
  };

  const value: SidebarContextType = {
    isSidebarOpen,
    toggleSidebar,
    setSidebarOpen,
    windowWidth,
    isMobile,
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};
